import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { uuid } from '@klp/utils/uuid';
import { FileObject } from '../classes';

type Props = {
    id: string;
    mimeTypes: string;
    queueName: string;
    fileUploadCallback: (file: FileObject) => void;
    disabled?: boolean;
};

export const FileUploadDynamic: React.FC<React.PropsWithChildren<Props>> = ({
    id,
    mimeTypes,
    queueName,
    fileUploadCallback,
    children,
    disabled,
}) => {
    const dropRef = useRef<HTMLLabelElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [dragging, setDragging] = useState(false);

    const handleFileInputClick = () => {
        fileInputRef.current?.click();
    };

    const handleDrag = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragIn = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.items?.length) {
            setDragging(true);
        }
    };

    const handleDragOut = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };

    const handleDrop = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer?.files || []);
        const accepted = files.filter((file) => mimeTypes.includes(file.type));
        accepted.forEach(handleDispatchUploadQueueAdd);
        e.dataTransfer?.clearData();
        setDragging(false);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        Array.from(e.target.files || []).forEach(handleDispatchUploadQueueAdd);
        e.target.value = '';
    };

    const handleDispatchUploadQueueAdd = (file: File) => {
        const fileObject = new FileObject({
            queueName,
            id: uuid(),
            fileName: file.name,
            fileContent: '',
            local: true,
            type: file.type,
        });

        const reader = new FileReader();
        reader.onload = async (ev) => {
            const result = ev.target?.result;
            if (typeof result === 'string') {
                fileObject.fileContent = result.split(',')[1]; // base64
                await fileUploadCallback(new FileObject(fileObject));
            }
        };
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        const el = dropRef.current;
        if (!el) return;

        el.addEventListener('dragenter', handleDragIn);
        el.addEventListener('dragleave', handleDragOut);
        el.addEventListener('dragover', handleDrag);
        el.addEventListener('drop', handleDrop);

        return () => {
            el.removeEventListener('dragenter', handleDragIn);
            el.removeEventListener('dragleave', handleDragOut);
            el.removeEventListener('dragover', handleDrag);
            el.removeEventListener('drop', handleDrop);
        };
    }, []);

    const classNames = classnames('Gallery__ItemPicture--label', {
        'Gallery__ItemPicture--dragover': dragging,
    });

    return (
        <div onClick={handleFileInputClick}>
            <input
                accept={mimeTypes}
                style={{ display: 'none' }}
                id={id}
                multiple={true}
                type="file"
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={disabled}
            />
            <label ref={dropRef} htmlFor={id} className={classNames}>
                {children}
            </label>
        </div>
    );
};
